'use client';

import type { ComponentProps } from 'react';
import useIsMobileSizedScreen from '@alltrails/denali/hooks/useIsMobileSizedScreen';
import type { Activity } from '@alltrails/types/Activity';
import PhotoSizeDimensions from '@alltrails/types/PhotoSizeDimensions';
import StaticMapSize from '@alltrails/types/StaticMapSize';
import useMapPhotos from '../../hooks/useMapPhotos';
import type { ActivityCardVariant, CardProps, InternalCardProps } from '../../types';
import { getActivityUrl } from '../../utils/cardLinks';
import ActivityMetadata from '../ActivityMetadata';
import SmallCard from '../SmallCard';
import StandardCard from '../StandardCard';

const getPhotoSize = (variant: ActivityCardVariant, isMobile: boolean): PhotoSizeDimensions => {
  switch (variant) {
    case 'small':
      return '160x160'; // LargeSquare
    case 'feed':
      return isMobile ? '1080x700' : '2048x2048'; // LargerWide : ExtraLarge
    case 'standard':
    default:
      return '1080x700'; // LargerWide
  }
};

const getMapSize = (variant: ActivityCardVariant, hasPhotos?: boolean): StaticMapSize => {
  switch (variant) {
    case 'small':
      return '160x160';
    case 'standard':
    case 'feed':
    default:
      return hasPhotos ? '160x160' : '320x320';
  }
};

const getMaxPhotos = (variant: ActivityCardVariant, maxPhotos: number) => {
  switch (variant) {
    case 'small':
      return 1;
    case 'standard':
    case 'feed':
    default:
      return maxPhotos;
  }
};

export type ActivityCardProps = CardProps & {
  variant: ActivityCardVariant;
  activity: Activity;
  blockNavigation?: boolean;
  isFirstParty?: boolean;
  showActivityType?: boolean;
  showAnonymizedUser?: boolean;
  showOnlyActivityType?: boolean;
  role?: string;
  ariaLabel?: string;
  headingComponent?: ComponentProps<typeof ActivityMetadata>['headingComponent'];
};

const ActivityCard = ({
  className,
  blockNavigation,
  variant,
  activity,
  isFirstParty = false,
  languageRegionCode,
  onCardClick,
  onCardMouseEnter,
  onCardMouseLeave,
  onFavoriteClick,
  isFavorite,
  openInNewTab,
  useMetric,
  maxPhotos = 6,
  baseUrl = '',
  deleteButton,
  adminCTAs,
  showActivityType,
  showAnonymizedUser,
  showOnlyActivityType,
  role,
  ariaLabel,
  headingComponent
}: ActivityCardProps) => {
  const { name, slug, id, hasPhotos } = activity;
  const href = getActivityUrl(slug, baseUrl, languageRegionCode);
  const isMobile = useIsMobileSizedScreen();
  const { staticMap, photos, loadPhotos } = useMapPhotos(
    id,
    getPhotoSize(variant, isMobile),
    getMapSize(variant, hasPhotos),
    getMaxPhotos(variant, maxPhotos),
    baseUrl
  );

  const onMouseEnter = () => {
    loadPhotos();
    onCardMouseEnter?.();
  };

  const metadata = (
    <ActivityMetadata
      variant={variant}
      activity={activity}
      isFirstParty={isFirstParty}
      languageRegionCode={languageRegionCode}
      useMetric={useMetric}
      baseUrl={baseUrl}
      openInNewTab={openInNewTab}
      showOnlyActivityType={showOnlyActivityType}
      showAnonymizedUser={showAnonymizedUser}
      showActivityType={showActivityType}
      averageTimeToComplete={activity?.averageTimeToComplete}
      headingComponent={headingComponent}
    />
  );

  const cardProps: InternalCardProps = {
    className,
    blockNavigation,
    name,
    href,
    openInNewTab,
    metadata,
    onCardClick,
    onCardMouseEnter: onMouseEnter,
    onCardMouseLeave,
    onFavoriteClick,
    isFavorite,
    role,
    ariaLabel
  };

  switch (variant) {
    case 'small':
      return <SmallCard {...cardProps} photo={hasPhotos ? photos[0] : staticMap} adminCTAs={adminCTAs} />;
    case 'standard':
    case 'feed':
    default:
      return (
        <StandardCard
          {...cardProps}
          photos={hasPhotos ? photos : [staticMap]}
          pinnedPhoto={hasPhotos ? staticMap : undefined}
          loadPhotos={loadPhotos}
          squarePhoto
          deleteButton={deleteButton}
        />
      );
  }
};

export default ActivityCard;
